<template>
  <div class="admin-translations">
    <h1>Translation Management</h1>

    <!-- Statistics Summary -->
    <div class="summary-cards">
      <div class="summary-card">
        <h3>LANGUAGES</h3>
        <div class="card-content">
          <div class="metric">
            <p class="summary-number">{{ statistics.activeLanguages || 0 }}</p>
            <p class="summary-label">Active</p>
          </div>
          <div class="metric">
            <p class="summary-number">{{ statistics.totalLanguages || 0 }}</p>
            <p class="summary-label">Total</p>
          </div>
        </div>
      </div>
      <div class="summary-card">
        <h3>UI TRANSLATIONS</h3>
        <div class="card-content">
          <div class="metric">
            <p class="summary-number">{{ statistics.totalUITranslations || 0 }}</p>
            <p class="summary-label">Total</p>
          </div>
        </div>
      </div>
      <div class="summary-card">
        <h3>QUESTIONS</h3>
        <div class="card-content">
          <div class="metric">
            <p class="summary-number">{{ statistics.totalQuestionTranslations || 0 }}</p>
            <p class="summary-label">Translated</p>
          </div>
          <div class="metric">
            <p class="summary-number">{{ statistics.totalQuestions || 0 }}</p>
            <p class="summary-label">Total</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab Navigation -->
    <div class="tab-navigation">
      <button
        :class="['tab-btn', { active: activeTab === 'languages' }]"
        @click="activeTab = 'languages'"
      >
        Languages
      </button>
      <button
        :class="['tab-btn', { active: activeTab === 'ui-translations' }]"
        @click="activeTab = 'ui-translations'"
      >
        UI Translations
      </button>
      <button
        :class="['tab-btn', { active: activeTab === 'question-translations' }]"
        @click="activeTab = 'question-translations'"
      >
        Question Translations
      </button>
    </div>

    <!-- Languages Tab -->
    <div v-if="activeTab === 'languages'" class="tab-content">
      <div class="section-header">
        <h2>Languages</h2>
        <button @click="showAddLanguageModal = true" class="add-btn">Add Language</button>
      </div>

      <div v-if="loadingLanguages" class="loading">Loading languages...</div>
      <div v-else class="data-table">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Native Name</th>
              <th>Status</th>
              <th>UI Translations</th>
              <th>Questions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="lang in languages" :key="lang.id">
              <td>
                <div v-if="editingLanguage?.id === lang.id" class="inline-edit">
                  <input v-model="editingLanguage.code" type="text" maxlength="10">
                </div>
                <div v-else @click="editLanguage(lang)" class="editable-cell">
                  {{ lang.code }}
                </div>
              </td>
              <td>
                <div v-if="editingLanguage?.id === lang.id" class="inline-edit">
                  <input v-model="editingLanguage.name" type="text">
                </div>
                <div v-else @click="editLanguage(lang)" class="editable-cell">
                  {{ lang.name }}
                </div>
              </td>
              <td>
                <div v-if="editingLanguage?.id === lang.id" class="inline-edit">
                  <input v-model="editingLanguage.nativeName" type="text">
                </div>
                <div v-else @click="editLanguage(lang)" class="editable-cell">
                  {{ lang.nativeName }}
                </div>
              </td>
              <td>
                <span :class="['status-badge', lang.isActive ? 'active' : 'inactive']">
                  {{ lang.isActive ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td>{{ lang.translationCount || 0 }}</td>
              <td>{{ lang.questionTranslationCount || 0 }}</td>
              <td>
                <div v-if="editingLanguage?.id === lang.id">
                  <button @click="saveLanguageEdit" class="save-inline-btn">Save</button>
                  <button @click="cancelLanguageEdit" class="cancel-inline-btn">Cancel</button>
                </div>
                <div v-else>
                  <button @click="editLanguage(lang)" class="edit-btn">Edit</button>
                  <button
                    @click="toggleLanguageStatus(lang)"
                    :class="['toggle-btn', lang.isActive ? 'deactivate' : 'activate']"
                  >
                    {{ lang.isActive ? 'Deactivate' : 'Activate' }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- UI Translations Tab -->
    <div v-if="activeTab === 'ui-translations'" class="tab-content">
      <div class="section-header">
        <h2>UI Translations</h2>
        <button @click="showAddTranslationModal = true" class="add-btn">Add Translation</button>
      </div>

      <div class="filters">
        <select v-model="selectedLanguageFilter" @change="loadUITranslations">
          <option value="">All Languages</option>
          <option v-for="lang in activeLanguages" :key="lang.id" :value="lang.id">
            {{ lang.name }}
          </option>
        </select>
        <select v-model="selectedSectionFilter" @change="filterTranslations">
          <option value="">All Sections</option>
          <option v-for="section in availableSections" :key="section" :value="section">
            {{ section.charAt(0).toUpperCase() + section.slice(1) }}
          </option>
        </select>
        <input
          v-model="searchQuery"
          @input="filterTranslations"
          type="text"
          placeholder="Search translations..."
          class="search-input"
        >
      </div>

      <div v-if="loadingUITranslations" class="loading">Loading translations...</div>
      <div v-else class="data-table">
        <table>
          <thead>
            <tr>
              <th>Language</th>
              <th>Section</th>
              <th>Key</th>
              <th>Value</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="trans in filteredUITranslations" :key="trans.id">
              <td>{{ trans.language?.name }}</td>
              <td>
                <span class="section-badge">
                  {{ trans.translationKey.split('.')[0].charAt(0).toUpperCase() + trans.translationKey.split('.')[0].slice(1) }}
                </span>
              </td>
              <td>{{ trans.translationKey }}</td>
              <td>
                <div v-if="editingTranslation?.id === trans.id">
                  <input v-model="editingTranslation.translationValue" type="text" class="translation-input">
                  <div class="translation-buttons">
                    <button @click="saveTranslation" class="save-inline-btn">Save</button>
                    <button @click="cancelEdit" class="cancel-inline-btn">Cancel</button>
                  </div>
                </div>
                <div v-else @click="editTranslation(trans)" class="editable-cell">
                  {{ trans.translationValue }}
                </div>
              </td>
              <td>
                <button @click="editTranslation(trans)" class="edit-btn">Edit</button>
                <button @click="confirmDeleteTranslation(trans)" class="delete-btn">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Question Translations Tab -->
    <div v-if="activeTab === 'question-translations'" class="tab-content">
      <div class="section-header">
        <h2>Question Translations</h2>
      </div>

      <div class="filters">
        <select v-model="selectedQuestionLanguageFilter" @change="loadQuestionTranslations">
          <option value="">All Languages</option>
          <option v-for="lang in activeLanguages" :key="lang.id" :value="lang.id">
            {{ lang.name }}
          </option>
        </select>
      </div>

      <div v-if="loadingQuestionTranslations" class="loading">Loading question translations...</div>
      <div v-else class="data-table">
        <table>
          <thead>
            <tr>
              <th>Language</th>
              <th>Question ID</th>
              <th>Original Question</th>
              <th>Translated Question</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="trans in questionTranslations" :key="trans.id">
              <td>{{ trans.language?.name }}</td>
              <td>{{ trans.questionId }}</td>
              <td>{{ trans.question?.question }}</td>
              <td>
                <div v-if="editingQuestionTranslation?.id === trans.id">
                  <textarea v-model="editingQuestionTranslation.questionText" rows="3"></textarea>
                  <div class="translation-buttons">
                    <button @click="saveQuestionTranslation" class="save-inline-btn">Save</button>
                    <button @click="cancelEdit" class="cancel-inline-btn">Cancel</button>
                  </div>
                </div>
                <div v-else @click="editQuestionTranslation(trans)" class="editable-cell">
                  {{ trans.questionText }}
                </div>
              </td>
              <td>
                <button @click="editQuestionTranslation(trans)" class="edit-btn">Edit</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Add Language Modal -->
    <div v-if="showAddLanguageModal" class="modal-overlay" @click="showAddLanguageModal = false">
      <div class="modal" @click.stop>
        <h3>Add New Language</h3>
        <form @submit.prevent="addLanguage">
          <div class="form-group">
            <label>Language Code (e.g., 'fr', 'es'):</label>
            <input v-model="newLanguage.code" required maxlength="10">
          </div>
          <div class="form-group">
            <label>English Name:</label>
            <input v-model="newLanguage.name" required>
          </div>
          <div class="form-group">
            <label>Native Name:</label>
            <input v-model="newLanguage.nativeName" required>
          </div>
          <div class="modal-actions">
            <button type="submit" class="save-btn">Add Language</button>
            <button type="button" @click="showAddLanguageModal = false" class="cancel-btn">Cancel</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Add Translation Modal -->
    <div v-if="showAddTranslationModal" class="modal-overlay" @click="showAddTranslationModal = false">
      <div class="modal" @click.stop>
        <h3>Add New Translation</h3>
        <form @submit.prevent="addTranslation">
          <div class="form-group">
            <label>Language:</label>
            <select v-model="newTranslation.languageId" required>
              <option value="">Select Language</option>
              <option v-for="lang in activeLanguages" :key="lang.id" :value="lang.id">
                {{ lang.name }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>Translation Key:</label>
            <input v-model="newTranslation.translationKey" required>
          </div>
          <div class="form-group">
            <label>Translation Value:</label>
            <input v-model="newTranslation.translationValue" required>
          </div>
          <div class="modal-actions">
            <button type="submit" class="save-btn">Add Translation</button>
            <button type="button" @click="showAddTranslationModal = false" class="cancel-btn">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';

export default {
  name: 'AdminTranslations',
  setup() {
    const activeTab = ref('languages');
    const statistics = ref({});

    // Languages
    const languages = ref([]);
    const loadingLanguages = ref(false);
    const showAddLanguageModal = ref(false);
    const newLanguage = ref({ code: '', name: '', nativeName: '' });
    const editingLanguage = ref(null);

    // UI Translations
    const uiTranslations = ref([]);
    const filteredUITranslations = ref([]);
    const allTranslationsForSections = ref([]); // Keep all translations for section extraction
    const loadingUITranslations = ref(false);
    const selectedLanguageFilter = ref('');
    const selectedSectionFilter = ref('');
    const searchQuery = ref('');
    const showAddTranslationModal = ref(false);
    const newTranslation = ref({ languageId: '', translationKey: '', translationValue: '' });
    const editingTranslation = ref(null);

    // Question Translations
    const questionTranslations = ref([]);
    const loadingQuestionTranslations = ref(false);
    const selectedQuestionLanguageFilter = ref('');
    const editingQuestionTranslation = ref(null);

    const activeLanguages = computed(() => languages.value.filter(l => l.isActive));

    // Extract unique sections from ALL translation keys (not just filtered ones)
    // Only include proper section.key format, exclude question translations
    const availableSections = computed(() => {
      const validSections = ['home', 'receiving', 'storage', 'preparation', 'service', 'warewashing', 'assessment'];
      const sections = new Set();

      allTranslationsForSections.value.forEach(trans => {
        const key = trans.translationKey || '';
        // Must contain a dot and start with valid section name
        if (key.includes('.')) {
          const section = key.split('.')[0].toLowerCase();
          if (validSections.includes(section)) {
            sections.add(section);
          }
        }
      });

      // Return in specific order with proper capitalization
      const orderedSections = ['home', 'receiving', 'storage', 'preparation', 'service', 'warewashing', 'assessment'];
      return orderedSections.filter(s => sections.has(s));
    });

    const loadStatistics = async () => {
      try {
        const response = await axios.get('/storesafe/api/admin/translations/statistics', {
          withCredentials: true
        });
        statistics.value = response.data;
      } catch (err) {
        console.error('Error loading statistics:', err);
      }
    };

    const loadLanguages = async () => {
      try {
        loadingLanguages.value = true;
        const response = await axios.get('/storesafe/api/admin/translations/languages', {
          withCredentials: true
        });
        languages.value = response.data;
      } catch (err) {
        console.error('Error loading languages:', err);
        alert('Failed to load languages');
      } finally {
        loadingLanguages.value = false;
      }
    };

    const addLanguage = async () => {
      try {
        await axios.post('/storesafe/api/admin/translations/languages', newLanguage.value, {
          withCredentials: true
        });
        showAddLanguageModal.value = false;
        newLanguage.value = { code: '', name: '', nativeName: '' };
        await loadLanguages();
        await loadStatistics();
      } catch (err) {
        console.error('Error adding language:', err);
        alert(err.response?.data?.message || 'Failed to add language');
      }
    };

    const editLanguage = (language) => {
      editingLanguage.value = { ...language };
    };

    const saveLanguageEdit = async () => {
      try {
        await axios.put(`/storesafe/api/admin/translations/languages/${editingLanguage.value.id}`, {
          code: editingLanguage.value.code,
          name: editingLanguage.value.name,
          nativeName: editingLanguage.value.nativeName
        }, {
          withCredentials: true
        });
        editingLanguage.value = null;
        await loadLanguages();
        await loadStatistics();
      } catch (err) {
        console.error('Error updating language:', err);
        alert(err.response?.data?.message || 'Failed to update language');
      }
    };

    const cancelLanguageEdit = () => {
      editingLanguage.value = null;
    };

    const toggleLanguageStatus = async (language) => {
      try {
        await axios.put(`/storesafe/api/admin/translations/languages/${language.id}/toggle`, {}, {
          withCredentials: true
        });
        await loadLanguages();
        await loadStatistics();
      } catch (err) {
        console.error('Error toggling language:', err);
        alert('Failed to update language status');
      }
    };

    const loadUITranslations = async (preserveFilters = false) => {
      try {
        loadingUITranslations.value = true;
        const params = {};
        if (selectedLanguageFilter.value) {
          params.languageId = selectedLanguageFilter.value;
        }
        const response = await axios.get('/storesafe/api/admin/translations/ui-translations', {
          params,
          withCredentials: true
        });

        // Filter out question translations - only include proper section.key format
        const validSections = ['home', 'receiving', 'storage', 'preparation', 'service', 'warewashing', 'assessment'];
        const filteredData = response.data.filter(trans => {
          const key = trans.translationKey || '';
          if (!key.includes('.')) return false; // Must have a dot
          const section = key.split('.')[0].toLowerCase();
          return validSections.includes(section); // Must be a valid section
        });

        uiTranslations.value = filteredData;

        // On first load (no language filter), store all translations for section extraction
        if (!selectedLanguageFilter.value && allTranslationsForSections.value.length === 0) {
          allTranslationsForSections.value = filteredData;
        }

        // Only reset filters if not preserving them
        if (!preserveFilters) {
          selectedSectionFilter.value = '';
          searchQuery.value = '';
        }

        // Apply current filters
        filterTranslations();
      } catch (err) {
        console.error('Error loading UI translations:', err);
        alert('Failed to load UI translations');
      } finally {
        loadingUITranslations.value = false;
      }
    };

    const filterTranslations = () => {
      let filtered = uiTranslations.value;

      // Filter by section
      if (selectedSectionFilter.value) {
        filtered = filtered.filter(t => {
          const section = t.translationKey.split('.')[0];
          return section === selectedSectionFilter.value;
        });
      }

      // Filter by search query
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(t =>
          t.translationKey.toLowerCase().includes(query) ||
          t.translationValue.toLowerCase().includes(query)
        );
      }

      filteredUITranslations.value = filtered;
    };

    const addTranslation = async () => {
      try {
        await axios.post('/storesafe/api/admin/translations/ui-translations', newTranslation.value, {
          withCredentials: true
        });
        showAddTranslationModal.value = false;
        newTranslation.value = { languageId: '', translationKey: '', translationValue: '' };

        // Reload all translations to update sections list
        allTranslationsForSections.value = [];
        selectedLanguageFilter.value = '';
        await loadUITranslations();
        await loadStatistics();
      } catch (err) {
        console.error('Error adding translation:', err);
        alert(err.response?.data?.message || 'Failed to add translation');
      }
    };

    const editTranslation = (translation) => {
      editingTranslation.value = { ...translation };
    };

    const saveTranslation = async () => {
      try {
        await axios.put(
          `/storesafe/api/admin/translations/ui-translations/${editingTranslation.value.id}`,
          { translationValue: editingTranslation.value.translationValue },
          { withCredentials: true }
        );
        editingTranslation.value = null;
        // Preserve filters when reloading after save
        await loadUITranslations(true);
      } catch (err) {
        console.error('Error saving translation:', err);
        alert('Failed to save translation');
      }
    };

    const confirmDeleteTranslation = async (translation) => {
      if (!confirm(`Are you sure you want to delete this translation?`)) {
        return;
      }
      try {
        await axios.delete(`/storesafe/api/admin/translations/ui-translations/${translation.id}`, {
          withCredentials: true
        });
        // Preserve filters when reloading after delete
        await loadUITranslations(true);
        await loadStatistics();
      } catch (err) {
        console.error('Error deleting translation:', err);
        alert('Failed to delete translation');
      }
    };

    const loadQuestionTranslations = async () => {
      try {
        loadingQuestionTranslations.value = true;
        const params = {};
        if (selectedQuestionLanguageFilter.value) {
          params.languageId = selectedQuestionLanguageFilter.value;
        }
        const response = await axios.get('/storesafe/api/admin/translations/question-translations', {
          params,
          withCredentials: true
        });
        questionTranslations.value = response.data;
      } catch (err) {
        console.error('Error loading question translations:', err);
        alert('Failed to load question translations');
      } finally {
        loadingQuestionTranslations.value = false;
      }
    };

    const editQuestionTranslation = (translation) => {
      editingQuestionTranslation.value = { ...translation };
    };

    const saveQuestionTranslation = async () => {
      try {
        await axios.put(
          `/storesafe/api/admin/translations/question-translations/${editingQuestionTranslation.value.id}`,
          { questionText: editingQuestionTranslation.value.questionText },
          { withCredentials: true }
        );
        editingQuestionTranslation.value = null;
        await loadQuestionTranslations();
      } catch (err) {
        console.error('Error saving question translation:', err);
        alert('Failed to save question translation');
      }
    };

    const cancelEdit = () => {
      editingTranslation.value = null;
      editingQuestionTranslation.value = null;
    };

    onMounted(async () => {
      await loadStatistics();
      await loadLanguages();
      await loadUITranslations();
      await loadQuestionTranslations();
    });

    return {
      activeTab,
      statistics,
      languages,
      loadingLanguages,
      showAddLanguageModal,
      newLanguage,
      editingLanguage,
      addLanguage,
      editLanguage,
      saveLanguageEdit,
      cancelLanguageEdit,
      toggleLanguageStatus,
      uiTranslations,
      filteredUITranslations,
      loadingUITranslations,
      selectedLanguageFilter,
      selectedSectionFilter,
      searchQuery,
      showAddTranslationModal,
      newTranslation,
      editingTranslation,
      activeLanguages,
      availableSections,
      loadUITranslations,
      filterTranslations,
      addTranslation,
      editTranslation,
      saveTranslation,
      confirmDeleteTranslation,
      questionTranslations,
      loadingQuestionTranslations,
      selectedQuestionLanguageFilter,
      editingQuestionTranslation,
      loadQuestionTranslations,
      editQuestionTranslation,
      saveQuestionTranslation,
      cancelEdit
    };
  }
};
</script>

<style scoped>
.admin-translations {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

@media (max-width: 768px) {
  .summary-cards {
    grid-template-columns: 1fr;
  }
}

.summary-card {
  background: white;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ddd;
  text-align: center;
}

.summary-card h3 {
  margin: 0 0 20px 0;
  font-size: 16px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.card-content {
  display: flex;
  justify-content: space-around;
  gap: 20px;
}

.metric {
  flex: 1;
}

.summary-number {
  font-size: 3rem;
  font-weight: bold;
  color: #ee3e41;
  margin: 0;
  line-height: 1;
}

.summary-label {
  font-size: 16px;
  color: #333;
  margin: 0;
  font-weight: 500;
}

.tab-navigation {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  border-bottom: 2px solid #ddd;
}

.tab-btn {
  padding: 10px 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  border-bottom: 3px solid transparent;
  transition: all 0.3s;
}

.tab-btn:hover {
  color: #ee3e41;
}

.tab-btn.active {
  color: #ee3e41;
  border-bottom-color: #ee3e41;
}

.tab-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #ddd;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header h2 {
  margin: 0;
}

.add-btn {
  background: #28a745;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.add-btn:hover {
  background: #218838;
}

.loading {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
}

.data-table {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: #f8f9fa;
}

th {
  padding: 12px;
  text-align: left;
  font-weight: bold;
  color: #333;
  border-bottom: 2px solid #ddd;
}

td {
  padding: 12px;
  border-bottom: 1px solid #eee;
}

tr:hover {
  background: #f8f9fa;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
}

.status-badge.active {
  background: #d4edda;
  color: #155724;
}

.status-badge.inactive {
  background: #f8d7da;
  color: #721c24;
}

.section-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: #e3f2fd;
  color: #1976d2;
  text-transform: uppercase;
}

.toggle-btn {
  padding: 4px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.toggle-btn.activate {
  background: #28a745;
  color: white;
}

.toggle-btn.deactivate {
  background: #ffc107;
  color: #000;
}

.filters {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}

.filters select {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.search-input {
  flex: 1;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.editable-cell {
  cursor: pointer;
  padding: 4px;
  border-radius: 3px;
}

.editable-cell:hover {
  background: #e9ecef;
}

.inline-edit {
  display: flex;
  gap: 5px;
  align-items: center;
}

.inline-edit input,
.inline-edit textarea {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid #007bff;
  border-radius: 4px;
  font-size: 14px;
}

.save-inline-btn {
  padding: 4px 8px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.cancel-inline-btn {
  padding: 4px 8px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.edit-btn {
  padding: 4px 8px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 5px;
}

.delete-btn {
  padding: 4px 8px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  padding: 30px;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.save-btn {
  background: #28a745;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.cancel-btn {
  background: #6c757d;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
</style>
